'use client';

/**
 * useUsersMe — the one hook every authenticated page calls on mount.
 *
 * Backed by `GET /api/v1/users/me` (the full user + nested-org response),
 * overlaid with `GET /api/v1/auth/me` for the auth-owned fields
 * (`is_email_verified`, trial window, edition) that older backend builds
 * only expose there. The two fire in parallel and land as one `UserMe`.
 *
 * Why two requests: these endpoints were never aliases, whatever old
 * comments said. `/auth/me` is a thin 9-field auth handshake; `/users/me`
 * carries the real org (plan usage, seats, business profile). This hook
 * used to call ONLY `/auth/me` and fabricate the org from hardcoded
 * constants, which is what made Settings → Org Profile load blank and
 * save NULLs over real GSTIN/CIN/PAN data (user-panel API audit
 * 2026-08-25, Finding 1 — this file was ported from there with the same
 * defect). `/users/me` values win wherever both respond; the overlay only
 * fills what `/users/me` doesn't carry on older backends.
 *
 * Callers get `(user, isLoading, error)` and can gate UI on:
 *   - `user === undefined && isLoading` → skeleton
 *   - `error instanceof AuthError` → redirect to /sign-in
 *   - `user` → render
 *
 * Disabled while signed out so the query doesn't fire a 401 immediately
 * after logout.
 */
import { useQuery } from '@tanstack/react-query';

import { AuthError, NetworkError, useApi } from '@/lib/api';

import type { Plan, Role, UserMe, UserMeOrg } from './types';
import { useAuthToken } from './useAuthToken';

export const USERS_ME_QUERY_KEY = ['auth', 'me'] as const;

/** Wire shape of GET /auth/me — flat, auth-owned fields only. */
type RawAuthMe = {
  id?: string;
  email?: string;
  full_name?: string | null;
  role?: string;
  org_id?: string | null;
  is_email_verified?: boolean;
  trial_expires_at?: string | null;
  plan?: Plan | null;
  edition?: 'india' | 'global' | null;
  preferred_jurisdiction?: string | null;
};

/** Wire shape of GET /users/me — full user + nested org. Every field is
 *  optional so a backend build that predates any given field degrades to
 *  the same default the old adapter used, never to a crash. */
type RawUsersMe = Omit<Partial<UserMe>, 'org'> & {
  org?: Partial<UserMeOrg> | null;
};

function mergeUserMe(me: RawUsersMe | null, auth: RawAuthMe | null): UserMe {
  const rawOrg = me?.org ?? null;
  // Absent reads as "india" — matches the backend's own default-edition
  // convention (app/db/models/organization.py::_india_only_default).
  const edition = rawOrg?.edition ?? auth?.edition ?? 'india';
  const org: UserMeOrg = {
    id: rawOrg?.id ?? auth?.org_id ?? '',
    name: rawOrg?.name ?? '',
    slug: rawOrg?.slug ?? '',
    edition,
    plan: (rawOrg?.plan as Plan | undefined) ?? auth?.plan ?? 'trial',
    plan_id: rawOrg?.plan_id ?? null,
    plan_type: rawOrg?.plan_type ?? 'user',
    trial_expires_at: rawOrg?.trial_expires_at ?? auth?.trial_expires_at ?? null,
    billing_period: rawOrg?.billing_period ?? null,
    // Matches billing_router.currency_for_org: INR for india, USD for
    // global — never a hardcoded 'INR' regardless of edition.
    billing_currency:
      rawOrg?.billing_currency ?? (edition === 'global' ? 'USD' : 'INR'),
    local_billing_currency: rawOrg?.local_billing_currency ?? null,
    // Never derived client-side: the note only exists if the server said so.
    presentment_note: rawOrg?.presentment_note ?? null,
    contracts_used_this_month: rawOrg?.contracts_used_this_month ?? 0,
    contracts_per_month_limit: rawOrg?.contracts_per_month_limit ?? -1,
    research_messages_used_this_month: rawOrg?.research_messages_used_this_month,
    research_messages_per_month_limit: rawOrg?.research_messages_per_month_limit,
    max_users: rawOrg?.max_users ?? 1,
    subscription_expires_at: rawOrg?.subscription_expires_at ?? null,
    is_founding_member: rawOrg?.is_founding_member ?? false,
    // Business profile — the fields the Org Profile form edits. Passing
    // them through (instead of omitting them, as the old adapter did) is
    // what lets that form load the org's real saved values.
    entity_type: rawOrg?.entity_type ?? null,
    business_state: rawOrg?.business_state ?? null,
    industry: rawOrg?.industry ?? null,
    gstin: rawOrg?.gstin ?? null,
    cin: rawOrg?.cin ?? null,
    pan: rawOrg?.pan ?? null,
    employee_count: rawOrg?.employee_count ?? null,
    turnover_bracket: rawOrg?.turnover_bracket ?? null,
    preferred_jurisdiction: rawOrg?.preferred_jurisdiction ?? null,
    preferred_language: rawOrg?.preferred_language ?? null,
    // Absent on the wire today (see UserMeOrg.country) — passed through,
    // never guessed, so the recording-consent rule fires the day it lands.
    country: rawOrg?.country ?? null,
    // Org settings written by PATCH /org/profile. Passed through so the
    // settings forms read back the number / switch an admin saved — the
    // retention form used to read `undefined` here and show "keep" after
    // every save.
    document_retention: rawOrg?.document_retention ?? null,
    meeting_audio_retention_days: rawOrg?.meeting_audio_retention_days ?? null,
    draft_client_review_required: rawOrg?.draft_client_review_required ?? false,
    white_label: rawOrg?.white_label ?? null,
  };
  return {
    id: me?.id ?? auth?.id ?? '',
    email: me?.email ?? auth?.email ?? '',
    full_name: me?.full_name ?? auth?.full_name ?? null,
    avatar_url: me?.avatar_url ?? null,
    role: ((me?.role ?? auth?.role) as Role) ?? 'owner',
    profession: me?.profession ?? null,
    phone: me?.phone ?? null,
    job_title: me?.job_title ?? null,
    preferred_language: me?.preferred_language ?? 'en',
    // Absent means unknown, not India — a global org's user may genuinely
    // have no jurisdiction, and 'IN' here is exactly the leak CLAUDE.md's
    // editions rules warn against.
    preferred_jurisdiction:
      me?.preferred_jurisdiction ?? auth?.preferred_jurisdiction ?? null,
    is_active: me?.is_active ?? true,
    is_email_verified: me?.is_email_verified ?? auth?.is_email_verified ?? false,
    last_seen_at: me?.last_seen_at ?? null,
    created_at: me?.created_at ?? '',
    org,
    // Passed through, never synthesised: absent means "not known yet".
    workspaces: me?.workspaces ?? null,
  };
}

export function useUsersMe() {
  const api = useApi();
  const token = useAuthToken();
  return useQuery({
    queryKey: USERS_ME_QUERY_KEY,
    queryFn: async ({ signal }) => {
      // Parallel fetch. An AuthError from either must propagate (the
      // (app)/layout handler redirects to /sign-in on it); any OTHER
      // failure of ONE endpoint degrades to the other's data rather
      // than failing the whole session view.
      const [meResult, authResult] = await Promise.allSettled([
        api<RawUsersMe>('/users/me', { signal }),
        api<RawAuthMe>('/auth/me', { signal }),
      ]);
      for (const r of [meResult, authResult]) {
        if (r.status === 'rejected' && r.reason instanceof AuthError) throw r.reason;
      }
      if (meResult.status === 'rejected' && authResult.status === 'rejected') {
        // Both down for a non-auth reason — surface the primary failure.
        throw meResult.reason;
      }
      const me = meResult.status === 'fulfilled' ? meResult.value : null;
      const auth = authResult.status === 'fulfilled' ? authResult.value : null;
      return mergeUserMe(me, auth);
    },
    staleTime: 30_000,
    enabled: token !== null,
    // 401 means the token is bad — no point retrying (the AuthError
    // handler in (app)/layout.tsx will redirect to /sign-in).  Network
    // blips get a single retry so a momentary connectivity hiccup
    // doesn't bounce the user out.
    retry: (failureCount, error) => {
      if (error instanceof AuthError) return false;
      if (error instanceof NetworkError) return failureCount < 1;
      return false;
    },
  });
}
